import React, { Component, ReactNode } from 'react';
import { errorLogger } from '../../services/errorLogger';
import { statePreserver } from '../../services/statePreserver';

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorId: string;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  private readonly COMPONENT_NAME = 'AppErrorBoundary';

  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      errorId: ''
    };
  }

  static getDerivedStateFromError(error: Error): Partial<AppErrorBoundaryState> {
    return {
      hasError: true,
      error,
      errorId: `app_error_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log the critical app-level error
    errorLogger.logError(error, this.COMPONENT_NAME, {
      componentStack: errorInfo.componentStack,
      errorBoundary: 'AppLevel',
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    });

    // Try to preserve any critical user state
    this.preserveCriticalState(error);
  }

  private preserveCriticalState = (error: Error): void => {
    try {
      // Preserve any forms or important user input
      const forms = document.querySelectorAll('form');
      const formData: Record<string, unknown> = {};
      
      forms.forEach((form, index) => {
        const formElements = form.querySelectorAll('input, textarea, select');
        const formEntries: Record<string, string> = {};
        
        formElements.forEach((element) => {
          const input = element as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
          if (input.name || input.id) {
            const key = input.name || input.id;
            if (input.type !== 'password' && input.type !== 'hidden') {
              formEntries[key] = input.value;
            }
          }
        });
        
        if (Object.keys(formEntries).length > 0) {
          formData[`form_${index}`] = formEntries;
        }
      });

      if (Object.keys(formData).length > 0) {
        statePreserver.checkpoint('critical_app_state', {
          formData,
          error: error.message,
          timestamp: new Date().toISOString(),
          url: window.location.href
        });
      }
    } catch (preserveError) {
      console.warn('Failed to preserve critical state:', preserveError);
    }
  };

  private handleReset = (): void => {
    // Clear the error state
    this.setState({
      hasError: false,
      error: undefined,
      errorId: ''
    });
    
    // Force a hard reset if needed
    if (this.state.error) {
      window.location.reload();
    }
  };

  private handleRestart = (): void => {
    // Navigate to home and reload
    window.location.href = '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const hasPreservedState = statePreserver.hasCheckpoint('critical_app_state');

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-card border border-border rounded-lg shadow-lg p-6">
            <div className="text-center">
              <div className="text-6xl mb-4">🚨</div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Oops! Something went wrong
              </h1>
              <p className="text-muted-foreground mb-6">
                The app encountered an unexpected error. Don't worry - your data might be preserved.
              </p>
            </div>

            {hasPreservedState && (
              <div className="mb-4 p-3 bg-muted rounded border-l-4 border-primary">
                <div className="text-sm font-medium text-foreground mb-1">
                  💾 Your work is preserved
                </div>
                <div className="text-xs text-muted-foreground">
                  We've saved your recent work. It should be restored when you continue.
                </div>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={this.handleReset}
                className="w-full px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 font-medium"
              >
                Try Again
              </button>
              
              <button
                onClick={this.handleRestart}
                className="w-full px-4 py-2 bg-muted text-muted-foreground rounded hover:bg-muted/80"
              >
                Restart App
              </button>
            </div>

            <div className="mt-6 pt-4 border-t border-border">
              <details className="text-sm">
                <summary className="text-muted-foreground cursor-pointer hover:text-foreground">
                  Technical Details
                </summary>
                <div className="mt-2 p-3 bg-muted rounded text-xs font-mono">
                  <div><strong>Error ID:</strong> {this.state.errorId}</div>
                  {this.state.error && (
                    <>
                      <div><strong>Type:</strong> {this.state.error.name}</div>
                      <div><strong>Message:</strong> {this.state.error.message}</div>
                    </>
                  )}
                  <div><strong>Time:</strong> {new Date().toLocaleString()}</div>
                </div>
              </details>
            </div>

            <div className="mt-4 text-center">
              <p className="text-xs text-muted-foreground">
                If this keeps happening,{' '}
                <a 
                  href="mailto:hello@giorgie.ai" 
                  className="text-primary hover:underline"
                >
                  let us know
                </a>
                {' '}and include the error ID above.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}